import { Injectable } from "@angular/core";
import { Storage } from "@ionic/storage";
import { Events } from "ionic-angular";
import { GraphService } from "./graph.service";

declare var foobar;
declare var forge;

@Injectable()
export class BulletinSecretService {
  key = null;
  username_signature = null;
  keyname = null;
  keykeys = null;
  username = null;
  public_key = null;
  address = null;
  identityType: "local" | "operator" = "local";
  operatorHost = null;
  identity: any = {
    username: "",
    username_signature: "",
    public_key: "",
    address: "",
    type: "local",
  };
  constructor(private storage: Storage, public events: Events) {}

  isOperator() {
    return this.identityType === "operator" || (this.keyname || "").indexOf("operator-") === 0;
  }

  hasSession() {
    return !!(this.key || this.isOperator());
  }

  operatorStorageKey(username, host) {
    const safeHost = (host || "")
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\/+$/, "")
      .replace(/^https?:\/\//, "");
    return "operator-" + (username || "").trim() + "@" + safeHost;
  }

  shared_encrypt(shared_secret, message) {
    var key = forge.pkcs5.pbkdf2(
      forge.sha256.create().update(shared_secret).digest().toHex(),
      "salt",
      400,
      32
    );
    var cipher = forge.cipher.createCipher("AES-CBC", key);
    var iv = "";
    cipher.start({ iv: iv });
    cipher.update(forge.util.createBuffer(iv + message));
    cipher.finish();
    return cipher.output.toHex();
  }

  get() {
    return this.all()
      .then((keys: any) => {
        return this.setKeyName(keys);
      })
      .then(() => {
        return this.setKey();
      });
  }

  setKeyName(keys) {
    return new Promise((resolve, reject) => {
      keys.sort(function (a, b) {
        if (a.idx < b.idx) return -1;
        if (a.idx > b.idx) return 1;
        return 0;
      });
      if (!this.keyname) {
        this.storage.get("last-keyname").then((key) => {
          if (key && typeof key == "string") {
            this.keyname = key;
          } else {
            this.keyname = keys[0].idx;
          }
          resolve(keys);
        });
      } else {
        resolve(keys);
      }
    });
  }

  setKey() {
    return new Promise((resolve, reject) => {
      this.storage.get(this.keyname).then((stored) => {
        if (this.keyname && this.keyname.indexOf("operator-") === 0) {
          return this.applyOperatorRecord(stored).then(resolve).catch(reject);
        }
        this.identityType = "local";
        this.operatorHost = null;
        this.key = foobar.bitcoin.ECPair.fromWIF(stored);
        this.username = this.keyname.substr("usernames-".length);
        this.public_key = this.key.getPublicKeyBuffer().toString("hex");
        this.address = this.key.getAddress();
        this.username_signature = this.generate_username_signature();
        this.identity = {
          username: this.username,
          username_signature: this.username_signature,
          public_key: this.public_key,
          address: this.address,
          type: "local",
        };
        return resolve(null);
      });
    });
  }

  applyOperatorRecord(stored) {
    return new Promise((resolve, reject) => {
      let record = stored;
      if (typeof stored === "string") {
        try {
          record = JSON.parse(stored);
        } catch (e) {
          return reject("invalid operator identity");
        }
      }
      if (!record || record.type !== "operator") {
        return reject("invalid operator identity");
      }
      this.identityType = "operator";
      this.key = null;
      this.username = record.username || "";
      this.public_key = record.public_key || "";
      this.address = record.address || "";
      this.operatorHost = record.host || record.baseUrl || "";
      this.username_signature = record.username_signature || "";
      this.identity = {
        username: this.username,
        username_signature: this.username_signature,
        public_key: this.public_key,
        address: this.address,
        type: "operator",
        host: this.operatorHost,
        baseUrl: record.baseUrl || this.operatorHost,
      };
      return resolve(null);
    });
  }

  importOperator(opts) {
    return new Promise((resolve, reject) => {
      const username = (opts && opts.username) || "";
      const host = (opts && opts.host) || "";
      const baseUrl = (opts && opts.baseUrl) || host;
      if (!username) return reject("username missing");
      if (!host && !baseUrl) return reject("node host missing");
      this.keyname = this.operatorStorageKey(username, baseUrl || host);
      const record = {
        type: "operator",
        username: username,
        host: host || baseUrl,
        baseUrl: baseUrl || host,
        public_key: (opts && opts.public_key) || "",
        address: (opts && opts.address) || "",
        username_signature: (opts && opts.username_signature) || "",
      };
      this.storage
        .set("last-keyname", this.keyname)
        .then(() => this.storage.set(this.keyname, record))
        .then(() => this.applyOperatorRecord(record))
        .then(() => resolve(record))
        .catch(reject);
    });
  }

  updateOperatorIdentity(fields) {
    return new Promise((resolve, reject) => {
      if (!this.isOperator() || !this.keyname) return reject("not operator");
      this.storage.get(this.keyname).then((stored) => {
        let record = stored;
        if (typeof stored === "string") {
          try {
            record = JSON.parse(stored);
          } catch (e) {
            record = {};
          }
        }
        record = Object.assign({}, record || {}, fields || {}, { type: "operator" });
        this.storage
          .set(this.keyname, record)
          .then(() => this.applyOperatorRecord(record))
          .then(() => resolve(record))
          .catch(reject);
      });
    });
  }

  getSpendAddress() {
    if (this.isOperator()) {
      return this.address || (this.identity && this.identity.address) || "";
    }
    if (this.key && this.key.getAddress) {
      return this.key.getAddress();
    }
    return this.address || "";
  }

  cloneIdentity() {
    return JSON.parse(this.identityJson());
  }

  identityJson() {
    return JSON.stringify(this.identity, null, 4);
  }

  generate_username_signature() {
    if (!this.key) {
      return this.username_signature || "";
    }
    return foobar.base64.fromByteArray(
      this.key.sign(foobar.bitcoin.crypto.sha256(this.username)).toDER()
    );
  }

  set(key) {
    return new Promise((resolve, reject) => {
      this.keyname = key;
      return this.storage
        .set("last-keyname", key)
        .then(() => {
          return this.storage.remove("usernames-");
        })
        .then((key) => {
          return this.get();
        })
        .then(() => {
          return this.setKey();
        })
        .then(() => {
          return resolve(null);
        })
        .catch((err) => {
          return reject(err);
        });
    });
  }

  create(username) {
    return new Promise((resolve, reject) => {
      if (!username) return reject("username missing");
      this.keyname = "usernames-" + username;
      this.storage.set("last-keyname", this.keyname);

      this.username = username;
      this.key = foobar.bitcoin.ECPair.makeRandom();
      this.storage.set(this.keyname, this.key.toWIF());
      this.username_signature = this.generate_username_signature();
      return this.get().then(() => {
        return resolve(null);
      });
    });
  }

  import(keyWif, username) {
    return new Promise((resolve, reject) => {
      if (!username) return reject("username missing");
      this.keyname = "usernames-" + username;
      this.storage.set("last-keyname", this.keyname);

      this.username = username;
      this.storage.set(this.keyname, keyWif.trim());
      this.key = foobar.bitcoin.ECPair.fromWIF(keyWif.trim());
      this.username_signature = this.generate_username_signature();
      return this.get().then(() => {
        return resolve(null);
      });
    });
  }

  async keyToIdentity(key) {
    if (key.idx && key.idx.indexOf("operator-") === 0) {
      let record = key.key;
      if (typeof record === "string") {
        try {
          record = JSON.parse(record);
        } catch (e) {
          record = {};
        }
      }
      return {
        username: (record && record.username) || key.idx.substr("operator-".length),
        username_signature: (record && record.username_signature) || "",
        public_key: (record && record.public_key) || "",
        address: (record && record.address) || "",
        type: "operator",
        host: (record && record.host) || "",
      };
    }
    const privkey = foobar.bitcoin.ECPair.fromWIF(key.key);
    const username = key.idx.substr("usernames-".length);
    const public_key = privkey.getPublicKeyBuffer().toString("hex");
    return {
      username: username,
      username_signature: this.generate_username_signature(),
      public_key: public_key,
      address: await this.publicKeyToAddress(public_key),
      wif: key.key,
      type: "local",
    };
  }

  all() {
    return new Promise((resolve, reject) => {
      var keykeys = [];
      this.storage
        .forEach((value, key) => {
          if (key.substr(0, "usernames-".length) === "usernames-") {
            keykeys.push({ key: value, idx: key, type: "local" });
          } else if (key.substr(0, "operator-".length) === "operator-") {
            keykeys.push({ key: value, idx: key, type: "operator" });
          }
        })
        .then(() => {
          this.keykeys = keykeys;
          resolve(keykeys);
        });
    });
  }

  unset() {
    this.key = null;
    this.username_signature = null;
    this.keyname = null;
    this.keykeys = null;
    this.username = null;
    this.public_key = null;
    this.address = null;
    this.identityType = "local";
    this.operatorHost = null;
    this.identity = {
      username: "",
      username_signature: "",
      public_key: "",
      address: "",
      type: "local",
    };
  }

  async publicKeyToAddress(public_key) {
    return await foobar.bitcoin.ECPair.fromPublicKeyBuffer(
      foobar.Buffer.Buffer.from(public_key, "hex")
    ).getAddress();
  }

  decrypt(message) {
    var key = forge.pkcs5.pbkdf2(
      forge.sha256.create().update(this.key.toWIF()).digest().toHex(),
      "salt",
      400,
      32
    );
    var decipher = forge.cipher.createDecipher("AES-CBC", key);
    var enc = this.hexToBytes(message);
    decipher.start({ iv: enc.slice(0, 16) });
    decipher.update(forge.util.createBuffer(enc.slice(16)));
    decipher.finish();
    return decipher.output;
  }

  hexToBytes(s) {
    var arr = [];
    for (var i = 0; i < s.length; i += 2) {
      var c = s.substr(i, 2);
      arr.push(parseInt(c, 16));
    }
    return String.fromCharCode.apply(null, arr);
  }
}



// WEBPACK FOOTER //
// ./src/app/bulletinSecret.service.ts